import {
  Armchair,
  Check,
  ChevronRight,
  CircleGauge,
  ExternalLink,
  Gift,
  Heart,
  Images,
  LogOut,
  MonitorPlay,
  PersonStanding,
  Plus,
  RefreshCw,
  Save,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { Dialog } from "../components/Dialog";
import { TvPhotosAdmin } from "../components/admin/TvPhotosAdmin";
import type {
  AdminDashboard,
  AdminGift,
  AdminMemory,
  CareTeamMember,
  ProgramItem,
  SeatingGuest,
  SeatingTable,
  Witness,
} from "../types";
import { isWediaryConfigured } from "../wediary";

type AdminTab =
  | "overview"
  | "gifts"
  | "memories"
  | "photos"
  | "seating"
  | "settings";

const tabs: Array<{ id: AdminTab; label: string; icon: typeof CircleGauge }> = [
  { id: "overview", label: "Přehled", icon: CircleGauge },
  { id: "gifts", label: "Dárky", icon: Gift },
  { id: "memories", label: "Vzpomínky", icon: Heart },
  { id: "photos", label: "TV fotografie", icon: Images },
  { id: "seating", label: "Sezení", icon: Armchair },
  { id: "settings", label: "Nastavení", icon: Settings },
];

export function AdminPage() {
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<AdminTab>("overview");
  const [error, setError] = useState("");

  const loadDashboard = async () => {
    try {
      const result = await api<AdminDashboard>("/api/admin/dashboard");
      setDashboard(result);
      setError("");
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401) {
        setDashboard(null);
      } else {
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Administrace se nenačetla.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDashboard();
  }, []);

  if (loading) {
    return <div className="admin-loading">Načítám administraci…</div>;
  }

  if (!dashboard) {
    return <AdminLogin onAuthenticated={() => void loadDashboard()} />;
  }

  const logout = async () => {
    await api("/api/admin/logout", {
      method: "POST",
      csrfToken: dashboard.csrfToken,
    });
    setDashboard(null);
  };

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand">
          <span>M&T</span>
          <div>
            <strong>Administrace</strong>
            <small>Markéta & Tomáš</small>
          </div>
        </div>
        <nav>
          {tabs.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={tab === item.id ? "is-active" : ""}
                onClick={() => setTab(item.id)}
                title={item.label}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="admin-sidebar__bottom">
          <Link to="/tv" target="_blank">
            <MonitorPlay size={18} />
            Otevřít TV režim
          </Link>
          <button onClick={() => void logout()}>
            <LogOut size={18} />
            Odhlásit
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div>
            <span>Svatební web</span>
            <strong>{tabs.find((item) => item.id === tab)?.label}</strong>
          </div>
          <button
            className="icon-button"
            onClick={() => void loadDashboard()}
            title="Obnovit data"
            aria-label="Obnovit data"
          >
            <RefreshCw size={19} />
          </button>
        </header>
        {error && <div className="admin-alert">{error}</div>}
        {tab === "overview" && <Overview dashboard={dashboard} setTab={setTab} />}
        {tab === "gifts" && (
          <GiftsAdmin
            dashboard={dashboard}
            onChanged={() => void loadDashboard()}
          />
        )}
        {tab === "memories" && (
          <MemoriesAdmin
            dashboard={dashboard}
            onChanged={() => void loadDashboard()}
          />
        )}
        {tab === "photos" && (
          <TvPhotosAdmin
            dashboard={dashboard}
            onChanged={() => void loadDashboard()}
          />
        )}
        {tab === "seating" && (
          <SeatingAdmin
            dashboard={dashboard}
            onChanged={() => void loadDashboard()}
          />
        )}
        {tab === "settings" && (
          <SettingsAdmin
            dashboard={dashboard}
            onChanged={() => void loadDashboard()}
          />
        )}
      </main>
    </div>
  );
}

function AdminLogin({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({
          username: form.get("username"),
          password: form.get("password"),
        }),
      });
      onAuthenticated();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Přihlášení se nezdařilo.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <Link className="login-page__back" to="/">← Zpět na web</Link>
      <form className="login-panel" onSubmit={submit}>
        <div className="login-panel__mark">M&T</div>
        <span>Soukromý přístup</span>
        <h1>Administrace svatby</h1>
        <label>
          Uživatelské jméno
          <input name="username" defaultValue="admin" autoComplete="username" />
        </label>
        <label>
          Heslo
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            autoFocus
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="button button--primary" disabled={busy}>
          {busy ? "Přihlašuji…" : "Přihlásit"}
        </button>
      </form>
    </main>
  );
}

function Stat({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: string | number;
  label: string;
}) {
  return (
    <article className="admin-stat">
      <span>{icon}</span>
      <div>
        <strong>{value}</strong>
        <small>{label}</small>
      </div>
    </article>
  );
}

function Overview({
  dashboard,
  setTab,
}: {
  dashboard: AdminDashboard;
  setTab: (tab: AdminTab) => void;
}) {
  const pending = dashboard.memories.filter(
    (memory) => memory.status === "pending",
  ).length;
  const reserved = dashboard.gifts.filter(
    (gift) => gift.status === "reserved",
  ).length;
  const approvedForTv = dashboard.memories.filter(
    (memory) => Boolean(memory.approvedForTv),
  ).length;

  return (
    <div className="admin-content">
      <div className="admin-stats">
        <Stat icon={<Gift />} value={reserved} label="rezervovaných přání" />
        <Stat icon={<Heart />} value={pending} label="čeká na schválení" />
        <Stat icon={<MonitorPlay />} value={approvedForTv} label="vzpomínek v TV" />
      </div>
      <section className="admin-section">
        <header>
          <div>
            <h2>Co čeká na pozornost</h2>
            <p>Nejdůležitější změny před svatebním dnem.</p>
          </div>
        </header>
        <div className="admin-actions">
          <button onClick={() => setTab("memories")}>
            <Heart />
            <span>
              <strong>{pending} nových vzpomínek</strong>
              Schválit obsah pro web a TV
            </span>
            <ChevronRight />
          </button>
          <Link to="/tv" target="_blank">
            <MonitorPlay />
            <span>
              <strong>TV režim</strong>
              Spustit náhled promítání
            </span>
            <ChevronRight />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Status({ value }: { value: string }) {
  const labels: Record<string, string> = {
    available: "Volné",
    reserved: "Rezervované",
    approved: "Schválené",
    rejected: "Zamítnuté",
  };
  return <span className={`status status--${value}`}>{labels[value] || value}</span>;
}

function GiftsAdmin({
  dashboard,
  onChanged,
}: {
  dashboard: AdminDashboard;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<AdminGift | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminGift | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [error, setError] = useState("");

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      title: form.get("title"),
      description: form.get("description"),
      category: form.get("category"),
      sortOrder: Number(form.get("sortOrder")),
      active: form.get("active") === "on",
    };
    try {
      await api(editing === "new" ? "/api/admin/gifts" : `/api/admin/gifts/${editing?.id}`, {
        method: editing === "new" ? "POST" : "PUT",
        csrfToken: dashboard.csrfToken,
        body: JSON.stringify(payload),
      });
      setEditing(null);
      onChanged();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Přání nelze uložit.");
    }
  };

  const release = async (gift: AdminGift) => {
    await api(`/api/admin/gifts/${gift.id}/release`, {
      method: "POST",
      csrfToken: dashboard.csrfToken,
    });
    onChanged();
  };

  const remove = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    setDeleteError("");
    try {
      await api(`/api/admin/gifts/${deleting.id}`, {
        method: "DELETE",
        csrfToken: dashboard.csrfToken,
      });
      setDeleting(null);
      onChanged();
    } catch (requestError) {
      setDeleteError(
        requestError instanceof Error
          ? requestError.message
          : "Přání nelze smazat.",
      );
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <div className="admin-content">
      <section className="admin-section">
        <header>
          <div><h2>Přání a rezervace</h2><p>Jména rezervujících jsou viditelná jen zde.</p></div>
          <button className="button button--primary button--small" onClick={() => setEditing("new")}>
            <Plus size={17} /> Přidat přání
          </button>
        </header>
        <div className="admin-list">
          {dashboard.gifts.map((gift) => (
            <article className="admin-list__item" key={gift.id}>
              <div className="admin-list__icon"><Gift /></div>
              <div className="admin-list__copy">
                <strong>{gift.title}</strong>
                <span>{gift.description}</span>
              </div>
              <Status value={gift.status} />
              <span className="admin-list__meta">{gift.reservedBy || "—"}</span>
              <div className="admin-list__buttons">
                {gift.status === "reserved" && (
                  <button onClick={() => void release(gift)}>
                    Uvolnit rezervaci
                  </button>
                )}
                <button onClick={() => setEditing(gift)}>Upravit</button>
                <button
                  className="admin-list__delete"
                  onClick={() => {
                    setDeleteError("");
                    setDeleting(gift);
                  }}
                  aria-label={`Smazat přání ${gift.title}`}
                  title="Smazat přání"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      {editing && (
        <div className="admin-drawer">
          <header>
            <h3>{editing === "new" ? "Nové přání" : "Upravit přání"}</h3>
            <button className="icon-button" onClick={() => setEditing(null)}><X /></button>
          </header>
          <form className="form-stack" onSubmit={save}>
            <label>Název<input name="title" defaultValue={editing === "new" ? "" : editing.title} required /></label>
            <label>Popis<textarea name="description" defaultValue={editing === "new" ? "" : editing.description} rows={3} /></label>
            <div className="form-row">
              <label>Kategorie
                <select name="category" defaultValue={editing === "new" ? "spolecne" : editing.category}>
                  <option value="spolecne">Společné</option>
                  <option value="domov">Domov</option>
                  <option value="zazitek">Zážitek</option>
                  <option value="vzpominky">Vzpomínky</option>
                </select>
              </label>
              <label>Pořadí<input name="sortOrder" type="number" min="0" defaultValue={editing === "new" ? dashboard.gifts.length : editing.sortOrder} /></label>
            </div>
            <label className="checkbox"><input name="active" type="checkbox" defaultChecked={editing === "new" || Boolean(editing.active)} /> Zobrazit na webu</label>
            {error && <p className="form-error">{error}</p>}
            <button className="button button--primary"><Save size={17} /> Uložit</button>
          </form>
        </div>
      )}
      <Dialog
        open={Boolean(deleting)}
        title="Smazat přání?"
        onClose={() => {
          if (!deletingBusy) setDeleting(null);
        }}
      >
        <div className="admin-delete-confirmation">
          <p>
            Přání <strong>{deleting?.title}</strong> bude trvale odstraněno.
          </p>
          {deleting?.status === "reserved" && (
            <p className="admin-delete-confirmation__warning">
              Spolu s přáním se odstraní také rezervace hosta{" "}
              <strong>{deleting.reservedBy}</strong>.
            </p>
          )}
          {deleteError && <p className="form-error">{deleteError}</p>}
          <div className="admin-delete-confirmation__actions">
            <button
              className="button button--outline"
              disabled={deletingBusy}
              onClick={() => setDeleting(null)}
            >
              Ponechat
            </button>
            <button
              className="button button--danger"
              disabled={deletingBusy}
              onClick={() => void remove()}
            >
              <Trash2 size={17} />
              {deletingBusy ? "Mažu…" : "Smazat přání"}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function MemoriesAdmin({
  dashboard,
  onChanged,
}: {
  dashboard: AdminDashboard;
  onChanged: () => void;
}) {
  const update = async (
    memory: AdminMemory,
    status: AdminMemory["status"],
    approvedForTv = Boolean(memory.approvedForTv),
  ) => {
    await api(`/api/admin/memories/${memory.id}`, {
      method: "PUT",
      csrfToken: dashboard.csrfToken,
      body: JSON.stringify({ status, approvedForTv }),
    });
    onChanged();
  };

  return (
    <div className="admin-content">
      <section className="admin-section">
        <header>
          <div><h2>Vzpomínky ke schválení</h2><p>Soukromé zprávy se nikdy nezobrazí na webu ani v TV.</p></div>
        </header>
        <div className="moderation-list">
          {dashboard.memories.map((memory) => (
            <article key={memory.id}>
              <header>
                <div>
                  <strong>{memory.title}</strong>
                  <span>{memory.author} · {memory.visibility === "private" ? "soukromé" : "veřejné"}</span>
                </div>
                <Status value={memory.status} />
              </header>
              <p>{memory.body}</p>
              <footer>
                {memory.visibility === "public" && (
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(memory.approvedForTv)}
                      onChange={(event) =>
                        void update(memory, memory.status, event.target.checked)
                      }
                    />
                    Povolit v TV
                  </label>
                )}
                <div>
                  <button onClick={() => void update(memory, "rejected", false)}>Zamítnout</button>
                  <button className="button--approve" onClick={() => void update(memory, "approved")}>
                    <Check size={16} /> Schválit
                  </button>
                </div>
              </footer>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function emptySeatingGuest(): SeatingGuest {
  return { name: "", gender: "" };
}

function resizeGuestList(guests: SeatingGuest[], seatCount: number) {
  if (guests.length >= seatCount) return guests.slice(0, seatCount);
  return [
    ...guests,
    ...Array.from(
      { length: seatCount - guests.length },
      emptySeatingGuest,
    ),
  ];
}

function SeatingGenderIcon({
  gender,
}: {
  gender: "woman" | "man";
}) {
  return (
    <span
      className={`guest-gender-icon guest-gender-icon--${gender}`}
      aria-hidden="true"
    >
      <PersonStanding />
    </span>
  );
}

function SeatingAdmin({
  dashboard,
  onChanged,
}: {
  dashboard: AdminDashboard;
  onChanged: () => void;
}) {
  const [tables, setTables] = useState<SeatingTable[]>(
    dashboard.seatingTables ?? [],
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const duplicateNumbers = tables
    .map((table) => table.number)
    .filter((number, index, numbers) => numbers.indexOf(number) !== index);

  const updateTable = (
    tableIndex: number,
    update: (table: SeatingTable) => SeatingTable,
  ) => {
    setTables((current) =>
      current.map((table, index) =>
        index === tableIndex ? update(table) : table,
      ),
    );
    setMessage("");
  };

  const updateGuest = (
    tableIndex: number,
    guestIndex: number,
    patch: Partial<SeatingGuest>,
  ) => {
    updateTable(tableIndex, (table) => ({
      ...table,
      guests: table.guests.map((guest, index) =>
        index === guestIndex ? { ...guest, ...patch } : guest,
      ),
    }));
  };

  const addTable = () => {
    const usedNumbers = new Set(tables.map((table) => table.number));
    let nextNumber = 1;
    while (usedNumbers.has(nextNumber)) nextNumber += 1;
    const seatCount = 8;
    setTables((current) => [
      ...current,
      {
        number: nextNumber,
        seatCount,
        guests: Array.from({ length: seatCount }, emptySeatingGuest),
      },
    ]);
    setMessage("");
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await api("/api/admin/seating", {
        method: "PUT",
        csrfToken: dashboard.csrfToken,
        body: JSON.stringify(tables),
      });
      setMessage("Zasedací pořádek je uložený.");
      onChanged();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Zasedací pořádek se nepodařilo uložit.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-content seating-admin">
      <form onSubmit={save}>
        <section className="admin-section seating-admin__intro">
          <header>
            <div>
              <h2>Zasedací pořádek</h2>
              <p>
                Rozbalte stůl, nastavte kapacitu a postupně doplňte hosty.
                Prázdná místa zůstanou na webu bez jména.
              </p>
            </div>
            <button
              type="button"
              className="button button--outline button--small"
              disabled={tables.length >= 20}
              onClick={addTable}
            >
              <Plus size={17} /> Přidat stůl
            </button>
          </header>
          <div className="seating-admin__summary">
            <span>
              <strong>{tables.length}</strong>
              stolů
            </span>
            <span>
              <strong>
                {tables.reduce((sum, table) => sum + table.seatCount, 0)}
              </strong>
              míst
            </span>
            <span>
              <strong>
                {tables.reduce(
                  (sum, table) =>
                    sum +
                    table.guests.filter((guest) => guest.name.trim()).length,
                  0,
                )}
              </strong>
              vyplněných hostů
            </span>
          </div>
        </section>

        {duplicateNumbers.length > 0 && (
          <div className="admin-alert">
            Každý stůl musí mít unikátní číslo.
          </div>
        )}
        {error && <div className="admin-alert">{error}</div>}

        <div className="seating-admin__tables">
          {tables.map((table, tableIndex) => {
            const filledGuests = table.guests.filter((guest) =>
              guest.name.trim(),
            ).length;
            return (
              <details
                className="admin-section seating-admin__table"
                key={`${table.number}-${tableIndex}`}
              >
                <summary>
                  <span className="seating-admin__table-number">
                    {table.number}
                  </span>
                  <div>
                    <strong>Stůl {table.number}</strong>
                    <small>
                      {table.seatCount} míst · {filledGuests} vyplněno
                    </small>
                  </div>
                  <ChevronRight aria-hidden="true" />
                </summary>
                <div className="seating-admin__table-body">
                  <div className="seating-admin__table-settings">
                    <label>
                      Číslo stolu
                      <input
                        type="number"
                        min={1}
                        max={99}
                        required
                        value={table.number}
                        onChange={(event) =>
                          updateTable(tableIndex, (current) => ({
                            ...current,
                            number: Math.min(
                              99,
                              Math.max(1, Number(event.target.value)),
                            ),
                          }))
                        }
                      />
                    </label>
                    <label>
                      Počet míst
                      <input
                        type="number"
                        min={1}
                        max={16}
                        required
                        value={table.seatCount}
                        onChange={(event) => {
                          const seatCount = Math.min(
                            16,
                            Math.max(1, Number(event.target.value)),
                          );
                          updateTable(tableIndex, (current) => ({
                            ...current,
                            seatCount,
                            guests: resizeGuestList(
                              current.guests,
                              seatCount,
                            ),
                          }));
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="button button--danger button--small"
                      onClick={() =>
                        setTables((current) =>
                          current.filter((_, index) => index !== tableIndex),
                        )
                      }
                    >
                      <Trash2 size={16} /> Odebrat stůl
                    </button>
                  </div>

                  <div className="seating-admin__guests">
                    {table.guests.map((guest, guestIndex) => (
                      <div
                        className="seating-admin__guest"
                        key={guestIndex}
                      >
                        <span>{guestIndex + 1}</span>
                        <label>
                          Jméno hosta
                          <input
                            value={guest.name}
                            maxLength={120}
                            placeholder="Zatím nevyplněno"
                            onChange={(event) =>
                              updateGuest(tableIndex, guestIndex, {
                                name: event.target.value,
                              })
                            }
                          />
                        </label>
                        <fieldset
                          className={`seating-admin__gender${
                            guest.name && !guest.gender ? " is-invalid" : ""
                          }`}
                        >
                          <legend>Pohlaví</legend>
                          <label title="Žena">
                            <input
                              type="radio"
                              name={`table-${tableIndex}-guest-${guestIndex}`}
                              required={Boolean(guest.name)}
                              checked={guest.gender === "woman"}
                              onChange={() =>
                                updateGuest(tableIndex, guestIndex, {
                                  gender: "woman",
                                })
                              }
                            />
                            <span>
                              <SeatingGenderIcon gender="woman" />
                              Žena
                            </span>
                          </label>
                          <label title="Muž">
                            <input
                              type="radio"
                              name={`table-${tableIndex}-guest-${guestIndex}`}
                              required={Boolean(guest.name)}
                              checked={guest.gender === "man"}
                              onChange={() =>
                                updateGuest(tableIndex, guestIndex, {
                                  gender: "man",
                                })
                              }
                            />
                            <span>
                              <SeatingGenderIcon gender="man" />
                              Muž
                            </span>
                          </label>
                        </fieldset>
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            );
          })}
        </div>

        <div className="settings-save">
          {message && (
            <span>
              <Check size={17} /> {message}
            </span>
          )}
          <button
            className="button button--primary"
            disabled={busy || duplicateNumbers.length > 0}
          >
            <Save size={18} /> {busy ? "Ukládám…" : "Uložit sezení"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SettingsAdmin({
  dashboard,
  onChanged,
}: {
  dashboard: AdminDashboard;
  onChanged: () => void;
}) {
  const [program, setProgram] = useState<ProgramItem[]>(dashboard.program);
  const [careTeam, setCareTeam] = useState<CareTeamMember[]>(
    dashboard.careTeam ?? [],
  );
  const [witnesses, setWitnesses] = useState<Witness[]>(
    dashboard.site.witnesses,
  );
  const [message, setMessage] = useState("");

  const updateWitness = (index: number, patch: Partial<Witness>) => {
    setWitnesses((current) =>
      current.map((witness, witnessIndex) =>
        witnessIndex === index ? { ...witness, ...patch } : witness,
      ),
    );
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const site = {
      ...Object.fromEntries(
        [
          "firstName", "secondName", "eyebrow", "tagline", "weddingDate",
          "venue", "venueDetail", "mapUrl", "venueUrl", "dressCode", "story",
          "wediaryUrl",
        ].map((key) => [key, form.get(key)]),
      ),
      witnesses,
    };
    const tv = {
      intervalSeconds: Number(form.get("intervalSeconds")),
      showProgram: form.get("showProgram") === "on",
      showWediary: form.get("showWediary") === "on",
      announcement: form.get("announcement"),
    };
    await api("/api/admin/settings", {
      method: "PUT",
      csrfToken: dashboard.csrfToken,
      body: JSON.stringify({ site, program, careTeam, tv }),
    });
    setMessage("Nastavení je uložené.");
    onChanged();
  };

  return (
    <div className="admin-content">
      <form className="settings-form" onSubmit={save}>
        <section className="admin-section">
          <header><div><h2>Obsah webu</h2><p>Základní údaje hostů a hero sekce.</p></div></header>
          <div className="settings-grid">
            <label>Jméno nevěsty<input name="firstName" defaultValue={dashboard.site.firstName} /></label>
            <label>Jméno ženicha<input name="secondName" defaultValue={dashboard.site.secondName} /></label>
            <label>Horní nadpis<input name="eyebrow" defaultValue={dashboard.site.eyebrow} /></label>
            <label>Datum a čas<input name="weddingDate" type="datetime-local" defaultValue={dashboard.site.weddingDate.slice(0, 16)} /></label>
            <label className="settings-grid__full">Úvodní text<textarea name="tagline" rows={3} defaultValue={dashboard.site.tagline} /></label>
            <label>Místo<input name="venue" defaultValue={dashboard.site.venue} /></label>
            <label>Detail místa<input name="venueDetail" defaultValue={dashboard.site.venueDetail} /></label>
            <label>Odkaz na mapu<input name="mapUrl" type="url" defaultValue={dashboard.site.mapUrl} /></label>
            <label>Web místa<input name="venueUrl" type="url" defaultValue={dashboard.site.venueUrl} /></label>
            <label>Dress code<input name="dressCode" defaultValue={dashboard.site.dressCode} /></label>
            <label className="settings-grid__full">Příběh<textarea name="story" rows={5} defaultValue={dashboard.site.story} /></label>
          </div>
        </section>
        <section className="admin-section">
          <header>
            <div>
              <h2>Kontakty na svědky</h2>
              <p>Telefon je volitelný a bez něj se na webu zobrazí jen jméno.</p>
            </div>
          </header>
          <div className="witness-editor">
            {witnesses.map((witness, index) => (
              <div className="witness-editor__item" key={index}>
                <h3>Kontakt {index + 1}</h3>
                <div className="settings-grid">
                  <label>
                    Jméno svědka nebo svědkyně
                    <input
                      value={witness.name}
                      onChange={(event) =>
                        updateWitness(index, { name: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Telefonní číslo
                    <input
                      type="tel"
                      inputMode="tel"
                      placeholder="+420 123 456 789"
                      value={witness.phone}
                      onChange={(event) =>
                        updateWitness(index, { phone: event.target.value })
                      }
                    />
                    <small>Volitelné</small>
                  </label>
                  <fieldset className="segmented segmented--two settings-grid__full">
                    <legend>Koho zastupuje</legend>
                    <label>
                      <input
                        type="radio"
                        name={`witness-side-${index}`}
                        checked={witness.side === "marketa"}
                        onChange={() =>
                          updateWitness(index, { side: "marketa" })
                        }
                      />
                      <span>Za Markétu</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`witness-side-${index}`}
                        checked={witness.side === "tomas"}
                        onChange={() =>
                          updateWitness(index, { side: "tomas" })
                        }
                      />
                      <span>Za Tomáše</span>
                    </label>
                  </fieldset>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-section">
          <header>
            <div>
              <h2>MyWediary</h2>
              <p>Odkaz pro nahrávání, QR kód na webu a promítací režim.</p>
            </div>
            {isWediaryConfigured(dashboard.site.wediaryUrl) && (
              <a
                className="button button--outline button--small"
                href={dashboard.site.wediaryUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink size={17} /> Ověřit odkaz
              </a>
            )}
          </header>
          <div className="settings-grid">
            <label className="settings-grid__full">
              Soukromý odkaz pro hosty
              <input
                name="wediaryUrl"
                type="url"
                inputMode="url"
                placeholder="https://app.mywediary.com/…"
                defaultValue={dashboard.site.wediaryUrl}
              />
              <small>Vložte přímý odkaz z galerie, ne adresu hlavní stránky.</small>
            </label>
            <p className="settings-help settings-grid__full">
              QR kód vytváří web automaticky ze stejného odkazu. Přihlašovací
              údaje do MyWediary se nikde neukládají.
            </p>
          </div>
        </section>
        <section className="admin-section">
          <header>
            <div><h2>Program dne</h2><p>Pořadí a časy se projeví i v TV režimu.</p></div>
            <button
              type="button"
              className="button button--outline button--small"
              onClick={() => setProgram((items) => [...items, { time: "", title: "", detail: "" }])}
            >
              <Plus size={17} /> Bod programu
            </button>
          </header>
          <div className="program-editor">
            {program.map((item, index) => (
              <div key={index}>
                <input
                  aria-label="Čas"
                  value={item.time}
                  onChange={(event) =>
                    setProgram((items) =>
                      items.map((current, itemIndex) =>
                        itemIndex === index ? { ...current, time: event.target.value } : current,
                      ),
                    )
                  }
                />
                <input
                  aria-label="Název"
                  value={item.title}
                  onChange={(event) =>
                    setProgram((items) =>
                      items.map((current, itemIndex) =>
                        itemIndex === index ? { ...current, title: event.target.value } : current,
                      ),
                    )
                  }
                />
                <input
                  aria-label="Detail"
                  value={item.detail}
                  onChange={(event) =>
                    setProgram((items) =>
                      items.map((current, itemIndex) =>
                        itemIndex === index ? { ...current, detail: event.target.value } : current,
                      ),
                    )
                  }
                />
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => setProgram((items) => items.filter((_, itemIndex) => itemIndex !== index))}
                  title="Odebrat"
                  aria-label="Odebrat"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-section">
          <header>
            <div>
              <h2>Kdo se o nás bude starat</h2>
              <p>
                Na webu se zobrazí název a popisek, bez externích odkazů.
                Maximálně 5 položek.
              </p>
            </div>
            <button
              type="button"
              className="button button--outline button--small"
              disabled={careTeam.length >= 5}
              onClick={() =>
                setCareTeam((items) => [
                  ...items,
                  { title: "", description: "" },
                ])
              }
            >
              <Plus size={17} /> Přidat osobu
            </button>
          </header>
          <div className="care-team-editor">
            {careTeam.map((member, index) => (
              <div className="care-team-editor__item" key={index}>
                <span aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <label className="care-team-editor__title">
                  Název
                  <input
                    required
                    maxLength={120}
                    value={member.title}
                    onChange={(event) =>
                      setCareTeam((items) =>
                        items.map((current, itemIndex) =>
                          itemIndex === index
                            ? { ...current, title: event.target.value }
                            : current,
                        ),
                      )
                    }
                  />
                </label>
                <label className="care-team-editor__description">
                  Popisek
                  <textarea
                    required
                    maxLength={300}
                    rows={2}
                    value={member.description}
                    onChange={(event) =>
                      setCareTeam((items) =>
                        items.map((current, itemIndex) =>
                          itemIndex === index
                            ? { ...current, description: event.target.value }
                            : current,
                        ),
                      )
                    }
                  />
                </label>
                <button
                  className="icon-button"
                  type="button"
                  onClick={() =>
                    setCareTeam((items) =>
                      items.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                  title="Odebrat osobu"
                  aria-label={`Odebrat ${member.title || `položku ${index + 1}`}`}
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-section">
          <header><div><h2>TV režim</h2><p>Obsah a rychlost automatického promítání.</p></div></header>
          <div className="settings-grid">
            <label>Interval v sekundách<input name="intervalSeconds" type="number" min="8" max="120" defaultValue={dashboard.tv.intervalSeconds} /></label>
            <label className="settings-grid__full">Výzva pro hosty<input name="announcement" defaultValue={dashboard.tv.announcement} /></label>
            <label className="checkbox"><input name="showProgram" type="checkbox" defaultChecked={dashboard.tv.showProgram} /> Zobrazovat program</label>
            <label className="checkbox"><input name="showWediary" type="checkbox" defaultChecked={dashboard.tv.showWediary} /> Zobrazovat Wediary</label>
          </div>
        </section>
        <div className="settings-save">
          {message && <span><Check size={17} /> {message}</span>}
          <button className="button button--primary"><Save size={18} /> Uložit změny</button>
        </div>
      </form>
    </div>
  );
}
